import React, { useState, useEffect } from 'react';
import { logUserActivityQuietly } from './supabaseClient';

export default function SirolimusCalculator({user}) {
  // --- STEP 1 INPUTS: PATIENT PROFILE & STATE TRACKERS ---
  const [ageGroup, setAgeGroup] = useState('adult'); 
  const [formulation, setFormulation] = useState('tablet'); 
  const [gender, setGender] = useState('male');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState(''); 

  // FIXED: High-level component states prevent 'not defined' linter compilation crashes lower down
  const [thalfMin, setThalfMin] = useState(54);
  const [thalfMax, setThalfMax] = useState(70);
  const [thalfAvg, setThalfAvg] = useState(62);

  // --- STEP 2 INPUT SELECTION ---
  const [clinicalIndication, setClinicalIndication] = useState('kt_csa');

  // --- STEP 3 INPUTS & OUTPUTS (DOSE & POPULATION ESTIMATES) ---
  const [currentDose, setCurrentDose] = useState('');
  const [popPredictedCssMin, setPopPredictedCssMin] = useState('0.0');
  const [popPredictedCssMax, setPopPredictedCssMax] = useState('0.0');

  // --- STEP 4 INPUTS & OUTPUTS (LAB ASSAY & METABOLIC FINGERPRINT) ---
  const [measuredLevel, setMeasuredLevel] = useState('');
  const [metabolicStatus, setMetabolicStatus] = useState('');
  const [compText, setCompText] = useState('');

  // --- STEP 5 HIGH-PRECISION SANDBOX MODE STATES ---
  const [proposedDose, setProposedDose] = useState('');
  const [simulatedCss, setSimulatedCss] = useState('');

  // --- STEP 6 TARGET CONDITION CLASSIFIER DROP-DOWNS ---
  const [clinicalResponse, setClinicalResponse] = useState('good'); // 'good' or 'poor'
  const [complianceConfirmed, setComplianceConfirmed] = useState('yes'); // 'yes' or 'no'

  // --- CENTRAL OUTPUT BOUNDARIES COMPONENT STATE MAP ---
  const [outputs, setOutputs] = useState({
    ibw: '0.0',
    appliedWeight: '0.0',
    clMin: '0.00',
    clMax: '0.00',
    targetRangeLow: 4.0,
    targetRangeHigh: 12.0,
    targetRangeLabel: '4.0 - 12.0 mcg/L'
  });

  // Reusable styling templates matching dark-mode specifications
  const cardStyle = { background: '#1c2226', padding: '20px', borderRadius: '10px', border: '1px solid #2c3539', marginBottom: '20px', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '0.68rem', fontWeight: 'bold', color: '#95a5a6', marginBottom: '6px', letterSpacing: '0.3px' };
  const inputStyle = { width: '100%', padding: '10px', background: '#111416', border: '1px solid #34495e', borderRadius: '6px', color: '#fff', fontSize: '0.85rem', boxSizing: 'border-box' };

  useEffect(() => {
    const weightNum = parseFloat(weight);
    const heightNum = parseFloat(height);
    const doseNum = parseFloat(currentDose);
    const labNum = parseFloat(measuredLevel);

    if (!weightNum || weightNum <= 0) return;

    // 1. Ideal Body Weight (IBW) / Dosing Weight Correction Protocol
    let calculatedIbw = 0;
    let appliedWeight = weightNum;

    if (heightNum && heightNum > 0) {
      const heightInches = heightNum / 2.54;
      if (gender === 'male') {
        calculatedIbw = 50 + 2.3 * (heightInches - 60);
      } else {
        calculatedIbw = 45.5 + 2.3 * (heightInches - 60);
      }
      if (calculatedIbw < 0) calculatedIbw = 0;

      // Obesity protection safety capping logic switch
      if (calculatedIbw > 0 && weightNum > calculatedIbw) {
        appliedWeight = calculatedIbw;
      }
    }

    // 2. Dynamic Age-Stratified Elimination Half-Life State Synchronization
    const SIROLIMUS_HALF_LIFE_MATRIX = {
      adult: { min: 54, max: 70, average: 62 },
      pediatric: { min: 36, max: 48, average: 42 }
    };

    const selectedAgeProfile = SIROLIMUS_HALF_LIFE_MATRIX[ageGroup] || SIROLIMUS_HALF_LIFE_MATRIX.adult;
    
    // Assign properties directly to external states to keep rendering threads synchronized
    setThalfMin(selectedAgeProfile.min);
    setThalfMax(selectedAgeProfile.max);
    setThalfAvg(selectedAgeProfile.average);

    // 3. Select Population Apparent Oral Clearance Constants (mL/kg/hr)
    let minConstant = 76;
    let maxConstant = 202;

    if (ageGroup === 'adult') {
      if (formulation === 'tablet') {
        minConstant = 76; maxConstant = 202;
      } else {
        minConstant = 123; maxConstant = 223;
      }
    } else {
      minConstant = 218; maxConstant = 682; // Pediatric profile acceleration constants
    }

    // Convert apparent parameters into absolute hourly values (L/hr)
    const clMinCalc = (minConstant * appliedWeight) / 1000;
    const clMaxCalc = (maxConstant * appliedWeight) / 1000;

    // 4. Step 3: Population-Based Steady State Trough Floor Predictions
    let predCssMin = '0.0';
    let predCssMax = '0.0';
    if (doseNum && doseNum > 0) {
      predCssMin = ((doseNum / (clMaxCalc * 24)) * 1000).toFixed(1);
      predCssMax = ((doseNum / (clMinCalc * 24)) * 1000).toFixed(1);
    }

    // 5. Dynamic Handbook Target Window Assignment
    let low = 4.0;
    let high = 12.0;

    switch (clinicalIndication) {
      case 'kt_csa': low = 4.0; high = 12.0; break;
      case 'kt_low_under1y': low = 16.0; high = 24.0; break;
      case 'kt_low_over1y': low = 12.0; high = 20.0; break;
      case 'kt_high_over1y': low = 12.0; high = 20.0; break;
      case 'kt_high_under1y': low = 10.0; high = 15.0; break;
      case 'comb_tac_mmf': low = 6.0; high = 8.0; break;
      case 'sub_tac_mmf': low = 8.0; high = 12.0; break;
      case 'conv_tac_siro': low = 4.0; high = 6.0; break;
      case 'gvhd_prophylaxis': low = 3.0; high = 12.0; break;
      default: low = 4.0; high = 24.0;
    }

    // 6. Step 5: High-Precision Individual Linear Scaling Sandbox Core
    const doseProp = parseFloat(proposedDose);
    let calculatedSimCss = '0.0';

    if (doseProp && doseNum && labNum && labNum > 0) {
      calculatedSimCss = ((doseProp / doseNum) * labNum).toFixed(1);
    }
    setSimulatedCss(calculatedSimCss);

    // 7. Step 4: Metabolic Track Fingerprint Assessment & Deviations
    let concentrationComparisonText = '';
    let triageStatus = 'normal';

    if (doseNum && labNum && labNum > 0) {
      if (labNum < parseFloat(predCssMin)) {
        concentrationComparisonText = `📉 The actual level (${labNum} mcg/L) is LOWER than the expected population average (${predCssMin}–${predCssMax} mcg/L). The patient clears the drug faster than average. Check compliance or enzyme induction.`;
      } else if (labNum > parseFloat(predCssMax)) {
        concentrationComparisonText = `🔺 The actual level (${labNum} mcg/L) is HIGHER than the expected population average (${predCssMin}–${predCssMax} mcg/L). The patient clears the drug slower than average. Check for inhibition.`;
      } else {
        concentrationComparisonText = `🎯 The actual level (${labNum} mcg/L) aligns perfectly within standard population average boundaries (${predCssMin}–${predCssMax} mcg/L).`;
      }

      // Check current laboratory level against indication parameters
      if (labNum > high) triageStatus = 'high';
      else if (labNum < low) triageStatus = 'low';
      else triageStatus = 'normal';
    }

    // Push calculations safely out to active rendering state states
    setOutputs({
      ibw: calculatedIbw.toFixed(1),
      appliedWeight: appliedWeight.toFixed(1),
      clMin: clMinCalc.toFixed(2),
      clMax: clMaxCalc.toFixed(2),
      targetRangeLow: low,
      targetRangeHigh: high,
      targetRangeLabel: `${low.toFixed(1)} - ${high.toFixed(1)} mcg/L`
    });
    setPopPredictedCssMin(predCssMin);
    setPopPredictedCssMax(predCssMax);
    setCompText(concentrationComparisonText);
    setMetabolicStatus(triageStatus);

  }, [ageGroup, formulation, gender, weight, height, clinicalIndication, currentDose, measuredLevel, proposedDose]);
    
  return (
    <div style={{ background: '#111416', minHeight: '100vh', color: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', padding: '20px', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
        <button 
          type="button"
          className="no-print" 
          onClick={() => {
              const actualUserEmail = user && user.email ? user.email : 'anonymous-user';
              const trackingLabel = `${actualUserEmail}-printed-Sirolimus`;
              logUserActivityQuietly(trackingLabel); 
              window.print();
          }} 
          style={{
            padding: '10px 20px',
            backgroundColor: '#27ae60', 
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
          }}
        >
          🖨️ Print PDF Report
        </button>
      </div>
      {/* MODULE MAIN TITLE */}
      <div style={{ textAlign: 'center', marginBottom: '25px', borderBottom: '2px solid #16a085', paddingBottom: '15px' }}>
        <h2 style={{ margin: 0, fontSize: '1.8rem', color: '#16a085', fontWeight: '800' }}>SIROLIMUS TDM WORKSHEET</h2>
        <span style={{ fontSize: '0.8rem', color: '#95a5a6', textTransform: 'uppercase' }}>Precision Kinetic Range & Stratification Workspace</span>
      </div>

      {/* HANDBOOK SAMPLING STRATIFICATION PROTOCOL DISCLOSURE WRAPPER */}
      <details style={{ background: '#1c2226', border: '1px solid #34495e', borderRadius: '8px', padding: '12px', marginBottom: '20px', cursor: 'pointer' }}>
        <summary style={{ fontSize: '0.82rem', fontWeight: 'bold', color: '#16a085', outline: 'none', userSelect: 'none' }}>
          📋 Check Clinical Sampling Protocol (Click to expand)
        </summary>
        <div style={{ marginTop: '12px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '10px', cursor: 'default' }} onClick={(e) => e.stopPropagation()}>
          <h5 style={{ color: '#95a5a6', margin: '0 0 8px 0', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
            Optimal Sampling Windows (Post-Initiation / Dose Shifts):
          </h5>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
            <div style={{ background: '#111416', padding: '8px 12px', borderRadius: '4px' }}>
              <span style={{ fontSize: '0.65rem', color: '#7f8c8d', display: 'block' }}>Initial or adjustment Sampling ADULT:</span>
              <strong style={{ fontSize: '0.85rem', color: '#fff' }}>5 – 7 Days</strong>
            </div>
            <div style={{ background: '#111416', padding: '8px 12px', borderRadius: '4px' }}>
              <span style={{ fontSize: '0.65rem', color: '#7f8c8d', display: 'block' }}>Initial or adjustment Sampling PEDIATRIC:</span>
              <strong style={{ fontSize: '0.85rem', color: '#3498db' }}>3 – 5 Days</strong>
            </div>
          </div>
          <div style={{ background: '#111416', padding: '10px', borderRadius: '6px', fontSize: '0.75rem', lineHeight: '1.4', color: '#bdc3c7' }}>
            <div style={{ marginBottom: '6px' }}>
              <strong style={{ color: '#fff' }}>⏱️ Maintenance dose Sampling:</strong> Oral <strong>30 minutes before</strong> OR <strong>immediately prior</strong> to the next scheduled maintenance dose baseline.
            </div>
            <div style={{ borderTop: '1px dashed #2c3e50', paddingTop: '6px', marginTop: '6px' }}>
              <strong style={{ color: '#e74c3c' }}>⚠️ Suspected Systemic Toxicity:</strong> If clinical signs of toxicity are actively suspected at the bedside, blood assays may be drawn at <strong>any time (Random Assay Triage)</strong>.
            </div>
          </div>
        </div>
      </details>

      {/* STEP 1: PARAMETER CALCULATOR WITH DYNAMIC RANGE SELECTION */}
      <div style={cardStyle}>
        <h3 style={{ color: '#16a085', margin: '0 0 15px 0', fontSize: '1.1rem' }}>Step 1: Patient Pharmacokinetic Clearance Range Profile</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
          <div>
            <label style={labelStyle}>AGE GROUP POPULATION RISK PROFILE:</label>
            <select value={ageGroup} onChange={(e) => {
              setAgeGroup(e.target.value);
              if (e.target.value === 'pediatric') setFormulation('combined');
              else setFormulation('tablet');
            }} style={inputStyle}>
              <option value="adult">Adult Population Track</option>
              <option value="pediatric">Pediatric Population Track (Rapid Clearance Profile)</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>DOSAGE FORMULATION TYPE:</label>
            {ageGroup === 'adult' ? (
              <select value={formulation} onChange={(e) => setFormulation(e.target.value)} style={inputStyle}>
                <option value="tablet">Tablets (76 - 202 mL/kg/hr baseline)</option>
                <option value="solution">Oral Solution (123 - 223 mL/kg/hr baseline)</option>
              </select>
            ) : (
              <select style={{ ...inputStyle, opacity: 0.6, cursor: 'not-allowed', border: '1px solid #16a085' }} disabled>
                <option value="combined">Combined Formulation Profile (218 - 682 mL/kg/hr)</option>
              </select>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginBottom: '15px' }}>
          <div>
            <label style={labelStyle}>GENDER:</label>
            <select value={gender} onChange={(e) => setGender(e.target.value)} style={inputStyle}>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>ACTUAL BODY WEIGHT (KG):</label>
            <input type="number" placeholder="e.g. 60" value={weight} onChange={(e) => setWeight(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>HEIGHT (CM): [OPTIONAL]</label>
            <input type="number" placeholder="e.g. 165" value={height} onChange={(e) => setHeight(e.target.value)} style={inputStyle} />
          </div>
        </div>

        {/* COMPREHENSIVE OUTPUT MATRIX RANGE PANELS */}
        <div style={{ background: '#111416', padding: '15px', borderRadius: '8px', border: '1px solid #2c3539', marginTop: '10px' }}>
          <span style={{ fontSize: '0.68rem', color: '#95a5a6', display: 'block', fontWeight: 'bold', textTransform: 'uppercase' }}>
            Systemic Elimination Kinetics Baseline:
          </span>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '10px' }}>
            
            {/* DYNAMIC HALF LIFE TELESCOPE CONTAINER - FIXED: Variable anchors load perfectly */}
            <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px', borderRadius: '6px', borderLeft: '3px solid #3498db' }}>
              <span style={{ fontSize: '0.62rem', color: '#7f8c8d', display: 'block' }}>ESTIMATED HALF-LIFE (t½):</span>
              <strong style={{ fontSize: '1.1rem', color: '#fff' }}>
                {thalfAvg} <span style={{ fontSize: '0.75rem', fontWeight: 'normal', color: '#95a5a6' }}>hours</span>
              </strong>
              <span style={{ fontSize: '0.6rem', color: '#57606f', display: 'block', marginTop: '2px' }}>
                Reference Window: {thalfMin} – {thalfMax} hrs ({ageGroup === 'pediatric' ? 'Pediatric accelerated clearance profile' : 'Standard adult clearance profile'})
              </span>
            </div>

            <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px', borderRadius: '6px', borderLeft: '3px solid #16a085' }}>
              <span style={{ fontSize: '0.62rem', color: '#7f8c8d', display: 'block' }}>MEAN APP. ORAL CLEARANCE (CL/F):</span>
              <strong style={{ fontSize: '1.1rem', color: '#fff' }}>
                {outputs.clMin} – {outputs.clMax} <span style={{ fontSize: '0.75rem', fontWeight: 'normal', color: '#95a5a6' }}>L/hr</span>
              </strong>
            </div>

          </div>
        </div>
      </div>
      {/* STEP 2: HANDBOOK THERAPEUTIC INDICATION TARGET PICKER */}
      <div style={cardStyle}>
        <h3 style={{ color: '#16a085', margin: '0 0 15px 0', fontSize: '1.1rem' }}>Step 2: Handbook Indication & Target Concentration Selection</h3>
        <div style={{ marginBottom: '15px' }}>
          <label style={labelStyle}>TARGET THERAPEUTIC SPECIFICATION INDICATION:</label>
          <select value={clinicalIndication} onChange={(e) => setClinicalIndication(e.target.value)} style={inputStyle}>
            <optgroup label="Kidney Transplant Suites">
              <option value="kt_csa">Kidney Transplant - Concurrent with Cyclosporine (CSA) [4.0 - 12.0 mcg/L]</option>
              <option value="kt_low_under1y">Kidney Transplant - Low/Mod Risk (post-Cyclosporine withdrawal, &lt; 1 year) [16.0 - 24.0 mcg/L]</option>
              <option value="kt_low_over1y">Kidney Transplant - Low/Mod Risk (post-Cyclosporine withdrawal, &gt; 1 year) [12.0 - 20.0 mcg/L]</option>
              <option value="kt_high_under1y">Kidney Transplant - High Risk (with Cyclosporine, ≤ 1 year) [10.0 - 15.0 mcg/L]</option>
              <option value="kt_high_over1y">Kidney Transplant - High Risk (with Cyclosporine, &gt; 1 year) [12.0 - 20.0 mcg/L]</option>
            </optgroup>
            <optgroup label="Combination & Substitution Protocols">
              <option value="comb_tac_mmf">Combined with Tacrolimus + Mycophenolate Mofetil without steroids [6.0 - 8.0 mcg/L]</option>
              <option value="sub_tac_mmf">Substitution for Tacrolimus, with Mycophenolate Mofetil + steroids (4-8 weeks post-transplant) [8.0 - 12.0 mcg/L]</option>
              <option value="conv_tac_siro">Conversion from Tacrolimus to Sirolimus (&gt;6 months post-transplant) [4.0 - 6.0 mcg/L]</option>
            </optgroup>
            <optgroup label="Other Graft Protocols">
              <option value="gvhd_prophylaxis">Graft versus Host Disease(GvHD) prophylaxis in allogeneic stem cell transplant [3.0 - 12.0 mcg/L]</option>
            </optgroup>
          </select>
        </div>

        <div style={{ background: '#111416', padding: '12px', borderRadius: '6px' }}>
          <span style={{ fontSize: '0.7rem', color: '#95a5a6', display: 'block' }}>STRATIFIED CLINICAL TARGET CEILING:</span>
          <strong style={{ fontSize: '1.3rem', color: '#00d2ff' }}>{outputs.targetRangeLabel}</strong>
        </div>
      </div>

      {/* STEP 3: DOSAGE PREDICTION & INITIAL POPULATION SUITABILITY TARGET */}
      <div style={cardStyle}>
        <h3 style={{ color: '#16a085', margin: '0 0 15px 0', fontSize: '1.1rem' }}>Step 3: Dosage Prediction & Population Suitability Target</h3>
        
        <div style={{ marginBottom: '15px' }}>
          <label style={labelStyle}>CURRENT DAILY MAINTENANCE DOSE (MG/DAY):</label>
          <input 
            type="number" 
            step="0.25" 
            placeholder="e.g. 2.0" 
            value={currentDose} 
            onChange={(e) => setCurrentDose(e.target.value)} 
            style={inputStyle} 
          />
        </div>

        {currentDose && (
          <div style={{ background: '#111416', padding: '15px', borderRadius: '8px', border: '1px solid #2c3539', textAlign: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '0.72rem', color: '#95a5a6', textTransform: 'uppercase', fontWeight: 'bold' }}>
              Estimated Baseline Population Trough Range (Css):
            </span>
            <div style={{ fontSize: '1.6rem', fontWeight: '900', color: '#fff', margin: '4px 0' }}>
              {popPredictedCssMin} – {popPredictedCssMax} <span style={{ fontSize: '0.9rem', fontWeight: 'normal', color: '#95a5a6' }}>mcg/L</span>
            </div>
            <span style={{ fontSize: '0.65rem', color: '#7f8c8d', fontStyle: 'italic', display: 'block' }}>
              Regimen Proof Base: [Dose (mg) / (Population CL Range × 24 hours)] × 1000 = mcg/L
            </span>
          </div>
        )}
      </div>
      {/* STEP 4: CLINICAL CORRELATION & 3-WAY TDM TRIAGE */}
      <div style={cardStyle}>
        <h3 style={{ color: '#9b59b6', margin: '0 0 15px 0', fontSize: '1.1rem' }}>Step 4: Clinical Correlation & 3-Way TDM Triage</h3>
        
        <div style={{ marginBottom: '15px' }}>
          <label style={labelStyle}>ACTUAL MEASURED WHOLE BLOOD TROUGH LEVEL (C₀) (MCG/L):</label>
          <input 
            type="number" 
            step="0.1" 
            placeholder="Enter laboratory assay value" 
            value={measuredLevel} 
            onChange={(e) => setMeasuredLevel(e.target.value)} 
            style={{ ...inputStyle, border: measuredLevel ? '1px solid #9b59b6' : '1px solid #34495e' }}
            disabled={!currentDose}
          />
        </div>

        {measuredLevel && currentDose && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            
            {/* CLEAN TWO-COLUMN TELEMETRY GRID */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '15px' }}>
              
              {/* LEFT COLUMN: INDIVIDUAL PROFILE VS POPULATION EXPECTATION */}
              <div style={{ background: '#111416', padding: '15px', borderRadius: '8px', border: '1px solid #2c3539' }}>
                <span style={gridLabelStyle}>Metabolic Baseline Check:</span>
                <strong style={{ fontSize: '1.2rem', color: '#fff', display: 'block', margin: '6px 0' }}>
                  {measuredLevel}.0 mcg/L <span style={{ color: '#7f8c8d', fontSize: '0.8rem' }}>VS</span> <span style={{ color: '#2ecc71' }}>{popPredictedCssMin}–{popPredictedCssMax} mcg/L</span>
                </strong>
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#a2b9bc', lineHeight: '1.4' }}>
                  {compText}
                </p>
              </div>

              {/* RIGHT COLUMN: ACTUAL LEVEL VS HANDBOOK TARGET SPECS */}
              <div style={{ background: '#111416', padding: '15px', borderRadius: '8px', border: '1px solid #2c3539' }}>
                <span style={gridLabelStyle}>Handbook Target Safety Audit:</span>
                <strong style={{ fontSize: '1.2rem', color: '#00d2ff', display: 'block', margin: '6px 0' }}>
                  {measuredLevel}.0 mcg/L <span style={{ color: '#7f8c8d', fontSize: '0.8rem' }}>VS</span> <span style={{ color: '#16a085' }}>{outputs.targetRangeLabel}</span>
                </strong>
                
                {/* DIRECT ACTION GUIDELINES PRINTED BASED ON SAFETY STATUS */}
                {metabolicStatus === 'normal' && (
                  <span style={{ color: '#2ecc71', fontWeight: 'bold', fontSize: '0.78rem', display: 'block', marginTop: '5px' }}>
                    ✅ SAFE: Level sits perfectly inside the therapeutic window. Maintain regimen.
                  </span>
                )}
                {metabolicStatus === 'low' && (
                  <span style={{ color: '#f1c40f', fontWeight: 'bold', fontSize: '0.78rem', display: 'block', marginTop: '5px' }}>
                    ⚠️ WARNING: Sub-therapeutic coverage risk. Consider an upward titration.
                  </span>
                )}
                {metabolicStatus === 'high' && (
                  <span style={{ color: '#e74c3c', fontWeight: 'bold', fontSize: '0.78rem', display: 'block', marginTop: '5px' }}>
                    🚨 CRITICAL: Supra-therapeutic toxicity risk. Hold or down-titrate immediately.
                  </span>
                )}
              </div>

            </div> {/* Close grid */}

          </div>
        )}
        {/* STEP 5: SIMULATED DOSE FORECAST (INDIVIDUAL HIGH-PRECISION MODEL) */}
      <div style={cardStyle}>
        <h3 style={{ color: '#2ecc71', margin: '0 0 15px 0', fontSize: '1.1rem' }}>Step 5: Simulated Dose Forecast (Individual Patient Model)</h3>
        
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>PROPOSED NEW MAINTENANCE DOSE (MG/DAY):</label>
          <input 
            type="number" 
            step="0.25" 
            placeholder="Enter proposed target trial dose" 
            value={proposedDose} 
            onChange={(e) => setProposedDose(e.target.value)} 
            style={{ ...inputStyle, border: proposedDose ? '1px solid #2ecc71' : '1px solid #34495e' }} 
            disabled={!measuredLevel}
          />
        </div>

        {parseFloat(proposedDose) > 0 && (
          <div style={{ background: '#111416', padding: '15px', borderRadius: '8px', border: '1px solid #2c3539', marginBottom: '10px' }}>
            
            {/* VALUE TELEMETRY PINPOINT STRING */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ fontSize: '0.72rem', color: '#95a5a6', fontWeight: 'bold' }}>PREDICTED PATIENT STEADY STATE (Css):</span>
              <strong style={{ fontSize: '1.4rem', color: '#00d2ff' }}>{simulatedCss} <span style={{ fontSize: '0.8rem', fontWeight: 'normal', color: '#95a5a6' }}>mcg/L</span></strong>
            </div>

            {/* VISUAL SPECTRUM ROAD TRACK BAR */}
            <div style={{ position: 'relative', height: '14px', background: '#2c3e50', borderRadius: '7px', margin: '25px 0 25px 0', overflow: 'visible' }}>
              
              {/* TARGET THERAPEUTIC REGION SHADOW BOUNDARY HIGHLIGHT (Dynamic bounds based on Step 2 Selection) */}
              <div style={{ 
                position: 'absolute', 
                left: `${Math.max(0, Math.min(90, outputs.targetRangeLow * 4))}%`,  
                width: `${Math.max(10, Math.min(100, (outputs.targetRangeHigh - outputs.targetRangeLow) * 4))}%`, 
                height: '100%', 
                background: 'rgba(22, 160, 133, 0.22)', 
                borderLeft: '1px dashed #16a085', 
                borderRight: '1px dashed #16a085',
                borderRadius: '4px'
              }} />

              {/* ACTIVE PATIENT PINPOINT INDICATOR NODE (A sleek pulsing glowing bead indicator) */}
              <div style={{
                position: 'absolute',
                left: `${Math.max(0, Math.min(98, parseFloat(simulatedCss) * 4))}%`,
                transform: 'translateX(-50%)',
                width: '18px',
                height: '18px',
                top: '-2px',
                background: '#f1c40f',
                borderRadius: '50%',
                boxShadow: '0 0 12px #f1c40f',
                border: '2px solid #fff',
                transition: 'left 0.2s ease-out',
                zIndex: 10
              }} />
            </div>

            {/* GRAPH SCALING X-AXIS SCALE FOOTER LABELS */}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.62rem', color: '#7f8c8d', padding: '0 2px' }}>
              <span>0 mcg/L</span>
              <span style={{ color: '#16a085', fontWeight: 'bold' }}>Handbook Target Window: {outputs.targetRangeLabel}</span>
              <span>25 mcg/L</span>
            </div>

            {/* CLEAR INDIVIDUAL RATIO PROOF TRACE */}
            <div style={{ marginTop: '15px', fontSize: '0.62rem', color: '#57606f', borderTop: '1px solid rgba(255,255,255,0.03)', paddingTop: '8px', lineHeight: '1.3' }}>
              Formula Predict Css: (Proposed New Dose / Current Daily Dose) × Actual Measured Lab Level<br />
              Individual Patient Linear Ratio Trace: ({proposedDose} mg / {currentDose} mg) × {measuredLevel} mcg/L = {simulatedCss} mcg/L
            </div>

          </div>
        )}
      </div>
        {/* STEP 6: CLINICAL INTERPRETATION & RECOMMENDATION */}
        <div style={cardStyle}>
          <h3 style={{ color: '#3498db', margin: '0 0 15px 0', fontSize: '1.1rem' }}>Step 6: Clinical Interpretation & Action Plan</h3>
          
          {/* CONDITION INTERACTIVE SELECTORS */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label style={labelStyle}>PATIENT CLINICAL RESPONSE:</label>
              <select value={clinicalResponse} onChange={(e) => setClinicalResponse(e.target.value)} style={inputStyle}>
                <option value="good">Good Response (Asymptomatic / Graft Stable)</option>
                <option value="poor">Poor Response (Signs of Rejection / Active Symptoms)</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>MEDICATION ADHERENCE / COMPLIANCE:</label>
              <select value={complianceConfirmed} onChange={(e) => setComplianceConfirmed(e.target.value)} style={inputStyle} disabled={(parseFloat(measuredLevel) >= outputs.targetRangeLow && parseFloat(measuredLevel) <= outputs.targetRangeHigh) || !measuredLevel}>
                <option value="yes">Adherence Confirmed (Patient taking reliably)</option>
                <option value="no">Adherence Suspected Unconfirmed / Missed Doses</option>
              </select>
            </div>
          </div>

          {/* COMPREHENSIVE PHARMACIST CARE PLAN GENERATOR BANNER */}
          {measuredLevel && (() => {
            const val = parseFloat(measuredLevel);
            const lowTarget = outputs.targetRangeLow;
            const highTarget = outputs.targetRangeHigh;

            // 🔴 CASE 1: ABOVE RANGE (SUPRA-THERAPEUTIC)
            if (val > highTarget) {
              return (
                <div style={{ background: '#111416', padding: '20px', borderRadius: '8px', borderLeft: '5px solid #e74c3c' }}>
                  <span style={{ background: 'rgba(231,76,60,0.15)', color: '#ff7675', padding: '3px 8px', borderRadius: '4px', fontSize: '0.68rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                    Pharmacokinetic Assessment: SUPRA-THERAPEUTIC
                  </span>
                  <p style={{ fontSize: '0.82rem', color: '#bdc3c7', margin: '10px 0', lineHeight: '1.5' }}>
                    The measured whole blood trough level (<strong>{val} mcg/L</strong>) is above the safety ceiling limit (Target: {lowTarget} - {highTarget} mcg/L), exposing the patient to elevated toxicity risks.
                  </p>
                  <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '6px', marginTop: '10px' }}>
                    <strong style={{ color: '#e74c3c', fontSize: '0.82rem', display: 'block', marginBottom: '4px' }}>📋 PROPOSED PHARMACIST CARE PLAN:</strong>
                    <span style={{ fontSize: '0.8rem', color: '#fff', lineHeight: '1.4', display: 'block' }}>
                      <strong>CRITICAL INTERVENTION:</strong> Withhold dose or down-titrate the daily maintenance regimen immediately. <br />
                      🔍 <strong>Drug Interaction Audit:</strong> Check for concurrent administration of <strong>CYP3A4 inhibitors</strong> listed in your handbook (e.g., <strong>Diltiazem, Verapamil, Clarithromycin, Erythromycin, Fluconazole, Ketoconazole</strong>, or <strong>Grapefruit juice</strong> co-ingestion) which significantly decrease sirolimus clearance and trigger pooling toxicity.
                    </span>
                  </div>
                </div>
              );
            }

            // 🟡 CASE 2: BELOW RANGE (SUB-THERAPEUTIC)
            if (val < lowTarget) {
              if (clinicalResponse === 'good') {
                return (
                  <div style={{ background: '#111416', padding: '20px', borderRadius: '8px', borderLeft: '5px solid #f1c40f' }}>
                    <span style={{ background: 'rgba(241,196,15,0.15)', color: '#f1c40f', padding: '3px 8px', borderRadius: '4px', fontSize: '0.68rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                      Pharmacokinetic Assessment: SUB-THERAPEUTIC (CLINICALLY STABLE)
                    </span>
                    <p style={{ fontSize: '0.82rem', color: '#bdc3c7', margin: '10px 0', lineHeight: '1.5' }}>
                      The level (<strong>{val} mcg/L</strong>) is below the theoretical target range floor, but the patient exhibits a stable clinical graft presentation.
                    </p>
                    <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '6px', marginTop: '10px' }}>
                      <strong style={{ color: '#f1c40f', fontSize: '0.82rem', display: 'block', marginBottom: '4px' }}>📋 PROPOSED PHARMACIST CARE PLAN:</strong>
                      <span style={{ fontSize: '0.8rem', color: '#fff', lineHeight: '1.4', display: 'block' }}>
                        <strong>MAINTAIN &amp; MONITOR:</strong> Since the active clinical response is currently good, maintain the current dose layout but schedule an accelerated follow-up tracking level check. Instruct the patient to keep blood draws strictly timed immediately prior to doses to avoid false low readings.
                      </span>
                    </div>
                  </div>
                );
              } else {
                // Poor clinical response
                if (complianceConfirmed === 'yes') {
                  return (
                    <div style={{ background: '#111416', padding: '20px', borderRadius: '8px', borderLeft: '5px solid #e67e22' }}>
                      <span style={{ background: 'rgba(230,126,34,0.15)', color: '#e67e22', padding: '3px 8px', borderRadius: '4px', fontSize: '0.68rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                        Pharmacokinetic Assessment: SUB-THERAPEUTIC Rejection Threat
                      </span>
                      <p style={{ fontSize: '0.82rem', color: '#bdc3c7', margin: '10px 0', lineHeight: '1.5' }}>
                        The level (<strong>{val} mcg/L</strong>) is inadequate and the patient shows poor clinical tracking, raising the risk of acute organ rejection.
                      </p>
                      <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '6px', marginTop: '10px' }}>
                        <strong style={{ color: '#e67e22', fontSize: '0.82rem', display: 'block', marginBottom: '4px' }}>📋 PROPOSED PHARMACIST CARE PLAN:</strong>
                        <span style={{ fontSize: '0.8rem', color: '#fff', lineHeight: '1.4', display: 'block' }}>
                          <strong>TITRATE DOSAGE UPWARD:</strong> Compliance is confirmed reliable. Cautiously increase the maintenance dose layout (using Step 5 sandbox simulation guides). <br />
                          🔍 <strong>Drug Interaction Audit:</strong> Investigate if the patient is concurrently taking a <strong>CYP3A4 inducer</strong> from your handbook list (e.g., <strong>Rifampicin, Carbamazepine, Phenobarbitone, Phenytoin</strong>, or <strong>St. John's Wort</strong>), which accelerates systemic clearance and drops drug levels.
                        </span>
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div style={{ background: '#111416', padding: '20px', borderRadius: '8px', borderLeft: '5px solid #e74c3c' }}>
                      <span style={{ background: 'rgba(231,76,60,0.15)', color: '#ff7675', padding: '3px 8px', borderRadius: '4px', fontSize: '0.68rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                        Pharmacokinetic Assessment: ADHERENCE DEFICIT SUSPECTED
                      </span>
                      <p style={{ fontSize: '0.82rem', color: '#bdc3c7', margin: '10px 0', lineHeight: '1.5' }}>
                        The level is sub-therapeutic with inadequate clinical parameters, and medication adherence habits are unconfirmed.
                      </p>
                      <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '6px', marginTop: '10px' }}>
                        <strong style={{ color: '#ff7675', fontSize: '0.82rem', display: 'block', marginBottom: '4px' }}>📋 PROPOSED PHARMACIST CARE PLAN:</strong>
                        <span style={{ fontSize: '0.8rem', color: '#fff', lineHeight: '1.4', display: 'block' }}>
                          <strong>ADHERENCE COUNSELING PREREQUISITE:</strong> Do NOT scale up the dosage layout aggressively yet! Thoroughly counsel the patient on strict administration adherence. Confirm compliance over the next 5-7 days (allowing accumulation back to equilibrium) before drawing another level or adjusting doses.
                        </span>
                      </div>
                    </div>
                  );
                }
              }
            }

            // 🟢 CASE 3: PERFECTLY WITHIN RANGE
            return (
              <div style={{ background: '#111416', padding: '20px', borderRadius: '8px', borderLeft: '5px solid #2ecc71' }}>
                <span style={{ background: 'rgba(46,204,113,0.15)', color: '#2ecc71', padding: '3px 8px', borderRadius: '4px', fontSize: '0.68rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                  Pharmacokinetic Assessment: THERAPEUTIC TARGET ATTAINED
                </span>
                <p style={{ fontSize: '0.82rem', color: '#bdc3c7', margin: '10px 0', lineHeight: '1.5' }}>
                  The measured Whole Blood concentration (<strong>{val} mcg/L</strong>) balances inside your selected handbook indicator metrics (<strong>{outputs.targetRangeLabel}</strong>).
                </p>
                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '6px', marginTop: '10px' }}>
                  <strong style={{ color: '#2ecc71', fontSize: '0.82rem', display: 'block', marginBottom: '4px' }}>📋 PROPOSED PHARMACIST CARE PLAN:</strong>
                  <span style={{ fontSize: '0.8rem', color: '#fff', lineHeight: '1.4', display: 'block' }}>
                    {clinicalResponse === 'good' 
                      ? "<strong>MAINTAIN DOSE:</strong> Patient is responding well and levels are stable. Maintain the current maintenance dose regimen framework configuration."
                      : "<strong>CAUTIOUS TITRATION UPWARD OR RE-EVALUATION:</strong> Levels are within range but clinical response is sub-optimal. Consider a very conservative upward titration within the safe target ceiling, or reassess secondary clinical biomarkers."
                    }
                  </span>
                </div>
              </div>
            );
          })()}
        </div>
        <p style={{ margin: '12px 0 0 0', fontSize: '0.62rem', color: '#7f8c8d', fontStyle: 'italic', textAlign: 'center', lineHeight: '1.3' }}>
            *Evaluation derived from official Clinical Pharmacokinetics Handbook criteria. Patient-specific clinical conditions and absolute biomarker trends must always override automated population estimates prior to final intervention execution.
        </p>
      </div>
    </div>
  );
}

const gridLabelStyle = {
  fontSize: '0.68rem',
  color: '#95a5a6',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  display: 'block'
};

